import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import StakeTable from './StakeTable';
import { Button, Modal, FormInput, Toggle, Badge, FormMultiSelect } from '../../components/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

const fetchProvidersAPI = async () => {
  const token = localStorage.getItem('token');
  const res = await apiClient.get('/api/game-engine/admin/providers?show_all=true', { headers: { Authorization: `Bearer ${token}` } });
  return res.data.success ? res.data.data : [];
};

const fetchAggregatorsAPI = async () => {
  const token = localStorage.getItem('token');
  const res = await apiClient.get('/api/game-engine/admin/aggregators', { headers: { Authorization: `Bearer ${token}` } });
  return res.data.success ? res.data.data : [];
};

const fetchGroupsAPI = async () => {
  const token = localStorage.getItem('token');
  const res = await apiClient.get('/api/game-engine/admin/groups', { headers: { Authorization: `Bearer ${token}` } });
  return res.data.success ? res.data.data : [];
};

interface Category {
  id: number;
  name: string;
  sort_order: number;
  api_is_enabled: boolean;
  admin_is_enabled: boolean;
}

const fetchCategoriesAPI = async (): Promise<Category[]> => {
  const token = localStorage.getItem("token");
  const res = await apiClient.get(`/api/game-engine/admin/categories`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? res.data.data : [];
};

const Categories: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(50);

  const { data: categories = [], isLoading } = useQuery({ queryKey: ['categories'], queryFn: fetchCategoriesAPI });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Category>>({});
  const [isEdit, setIsEdit] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [manageGamesCategoryId, setManageGamesCategoryId] = useState<number | null>(null);
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  const [providerAggFilter, setProviderAggFilter] = useState<string>('');
  
  const { data: providers = [] } = useQuery({ queryKey: ['providers'], queryFn: fetchProvidersAPI });
  const { data: aggregators = [] } = useQuery({ queryKey: ['aggregators'], queryFn: fetchAggregatorsAPI });
  const { data: groups = [] } = useQuery({ queryKey: ['groups'], queryFn: fetchGroupsAPI });
  
  const [selectedAssignGroupId, setSelectedAssignGroupId] = useState<number | ''>('');

  const assignToGroupMutation = useMutation({
    mutationFn: async () => {
      if (!selectedAssignGroupId) throw new Error('No group selected');
      const token = localStorage.getItem('token');
      return apiClient.post(`/api/game-engine/admin/groups/${selectedAssignGroupId}/assign-games`, { category_ids: selectedIds }, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: (res) => {
      toast.success(res.data.message || 'Categories successfully assigned to group!');
      setSelectedIds([]); // clear selection
      setSelectedAssignGroupId('');
    },
    onError: () => toast.error('Failed to assign categories to group')
  });

  const assignMutation = useMutation({
    mutationFn: async (provider_ids: string[]) => {
      const token = localStorage.getItem('token');
      return apiClient.post(`/api/game-engine/admin/categories/${manageGamesCategoryId}/assign-games`, { provider_ids }, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: (res) => {
      toast.success(res.data.message || 'Games assigned to category!');
      setManageGamesCategoryId(null);
      setSelectedProviderIds([]);
    },
    onError: () => toast.error('Failed to assign games')
  });

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Category>) => {
      const token = localStorage.getItem("token");
      if (data.id) return apiClient.put(`/api/game-engine/admin/categories/${data.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      return apiClient.post(`/api/game-engine/admin/categories`, data, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success(variables.id ? "Category updated!" : "Category created!");
      closeModal();
    },
    onError: () => toast.error("Failed to save category")
  });

  const openModal = (cat?: Category) => {
    if (cat) { setFormData(cat); setIsEdit(true); }
    else { setFormData({ name: '', sort_order: 0, admin_is_enabled: true, api_is_enabled: true }); setIsEdit(false); }
    setIsModalOpen(true);
  };

  const closeModal = () => { setIsModalOpen(false); setFormData({}); };
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); saveMutation.mutate(formData); };

  const filtered = categories.filter((a: any) => !searchTerm || a.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(filtered.length / limit));
  const paginated = filtered.slice((page - 1) * limit, page * limit);

  const columns = [
    { key: 'id', label: 'ID', width: 'w-16' },
    { key: 'name', label: 'Name', render: (val: string, item: Category) => (
      <span onClick={() => navigate(`/game-engine/games?category_id=${item.id}`)} className="font-semibold text-slate-200 hover:text-indigo-400 hover:underline cursor-pointer">
        {val}
      </span>
    ) },
    { key: 'game_count', label: 'Games Count', render: (val: number, item: Category) => (
      <span onClick={() => navigate(`/game-engine/games?category_id=${item.id}`)} className="font-bold text-indigo-400 hover:text-indigo-300 hover:underline cursor-pointer">
        {val || 0}
      </span>
    ) },
    { key: 'sort_order', label: 'Sort Order' },
    {
      key: 'admin_is_enabled', label: 'Admin Status',
      render: (val: boolean, item: Category) => <Toggle checked={val} onChange={(c) => saveMutation.mutate({ ...item, admin_is_enabled: c })} />
    },
    {
      key: 'api_is_enabled', label: 'API Status',
      render: (val: boolean) => <Badge variant={val ? "success" : "danger"}>{val ? "Active" : "Down"}</Badge>
    },
    {
      key: 'actions', label: 'Actions', textAlign: 'right' as const,
      render: (_: any, item: Category) => (
        <div className="flex items-center justify-end gap-2">
                    <Button variant="primary" size="xs" onClick={() => setManageGamesCategoryId(item.id)}>Manage Games</Button>
          <Button variant="ghost" size="xs" onClick={() => openModal(item)}>Edit</Button>
        </div>
      )
    }
  ];

  return (
    <>
      <StakeTable
        title="Game Categories"
        columns={columns}
        data={paginated}
        isLoading={isLoading}
        searchPlaceholder="Search categories..."
        onSearch={(q) => { setSearchTerm(q); setPage(1); }}
        page={page}
        totalPages={totalPages}
        total={filtered.length}
        limit={limit}
        onLimitChange={setLimit}
        onPageChange={setPage}
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        emptyMessage="No categories found."
        inlineBottomActions={true}
        bottomActions={
          <div className="flex flex-wrap items-center gap-2.5 w-full">
            {selectedIds.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 bg-slate-800/50 p-1.5 rounded-lg border border-slate-700 w-full sm:w-auto">
                <span className="text-xs text-slate-300 font-medium whitespace-nowrap">{selectedIds.length} selected</span>
                <select
                  className="bg-slate-900 border border-slate-700 text-slate-300 text-xs rounded-md shadow-sm hover:bg-slate-800 focus:outline-none focus:border-indigo-500 py-1.5 px-2 transition-colors cursor-pointer"
                  value={selectedAssignGroupId}
                  onChange={(e) => setSelectedAssignGroupId(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="" className="bg-slate-900 text-slate-400">Group...</option>
                  {groups.map((g: any) => (
                    <option key={g.id} value={g.id} className="bg-slate-900 text-slate-200">{g.name}</option>
                  ))}
                </select>
                <Button 
                  variant="success" 
                  size="xs"
                  disabled={!selectedAssignGroupId || selectedIds.length === 0} 
                  loading={assignToGroupMutation.isPending}
                  onClick={() => assignToGroupMutation.mutate()}
                >
                  Assign to Group
                </Button>
              </div>
            )}
          </div>
        }
        actions={<Button variant="success" onClick={() => openModal()}>+ Add Category</Button>}
      />

            <Modal open={isModalOpen} onClose={closeModal} title={isEdit ? 'Edit Category' : 'Add Category'}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={closeModal}>Cancel</Button>
            <Button variant="primary" size="sm" loading={saveMutation.isPending} onClick={handleSubmit}>{isEdit ? 'Save Changes' : 'Create'}</Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput label="Name" required value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} />
          <FormInput type="number" label="Sort Order" required value={formData.sort_order || 0} onChange={e => setFormData({ ...formData, sort_order: parseInt(e.target.value) })} />
        </form>
      </Modal>

      <Modal open={manageGamesCategoryId !== null} onClose={() => setManageGamesCategoryId(null)} title="Assign Games to Category"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setManageGamesCategoryId(null)}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={selectedProviderIds.length === 0} loading={assignMutation.isPending} onClick={() => assignMutation.mutate(selectedProviderIds)}>Assign Provider Games</Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-300">Select one or more Providers to bulk-assign all their active games into this category. You can also assign games manually via the Games Management page.</p>
          
          {/* Aggregator Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 font-medium whitespace-nowrap">Filter by Aggregator:</span>
            <select
              className="flex-1 bg-slate-900 border border-slate-700 text-slate-200 rounded-md px-2.5 py-1.5 text-xs outline-none focus:border-indigo-500 transition-all"
              value={providerAggFilter}
              onChange={e => { setProviderAggFilter(e.target.value); setSelectedProviderIds([]); }}
            >
              <option value="">All Aggregators</option>
              {aggregators.map((a: any) => (
                <option key={a.id} value={String(a.id)}>{a.name}</option>
              ))}
            </select>
            {providerAggFilter && (
              <button
                onClick={() => { setProviderAggFilter(''); setSelectedProviderIds([]); }}
                className="text-xs text-slate-400 hover:text-slate-200 transition-colors px-1"
                title="Clear filter"
              >✕</button>
            )}
          </div>

          <div className="pb-32">
            <FormMultiSelect
              label="Providers"
              placeholder="Search and Select Providers"
              options={providers
                .filter((p: any) => p.developer_is_enabled)
                .filter((p: any) => providerAggFilter ? String(p.aggregator_id) === providerAggFilter : true)
                .map((prov: any) => ({
                  value: String(prov.id),
                  label: prov.aggregator_name ? `${prov.name} — ${prov.aggregator_name}` : prov.name
                }))}
              selectedValues={selectedProviderIds}
              onChange={setSelectedProviderIds}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};
export default Categories;
